import React, { useState } from "react";
import { Box, Menu, MenuItem, ListItemIcon, ListItemText, Paper, Skeleton, Typography, Grid, LinearProgress } from "@mui/material";
import {
  ArrowUpward as InsertAboveIcon,
  ArrowDownward as InsertBelowIcon,
  Edit as EditIcon,
  Delete as RemoveIcon,
  DragIndicator,
} from "@mui/icons-material";
import "moment-timezone";
import moment from "moment";

import { GetTimeZoneFromAbbreviation } from "common/Constants";
import EventInlineEdit from "./EventInlineEdit";

const VehicleActivityCards = (props) => {
  const numFormat4Decimals = new Intl.NumberFormat("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  const numFormat2Decimals = new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [contextEvent, setContextEvent] = useState<any>(null);
  const [contextIndex, setContextIndex] = useState<number>(-1);

  // Inline editing state
  const [editingEventId, setEditingEventId] = useState<any>(null);
  const [insertAt, setInsertAt] = useState<{ index: number; event: any } | null>(null);

  // Drag and drop state
  const [draggedEvent, setDraggedEvent] = useState<any>(null);
  const [dropTargetId, setDropTargetId] = useState<any>(null);
  const [dropPosition, setDropPosition] = useState<"top" | "bottom">("bottom");

  const handleContextMenu = (e: React.MouseEvent, event: any, index: number) => {
    e.preventDefault();
    setContextMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
    setContextEvent(event);
    setContextIndex(index);
  };

  const handleContextClose = () => {
    setContextMenu(null);
    setContextEvent(null);
    setContextIndex(-1);
  };

  const handleInsert = (position: "above" | "below") => {
    if (!contextEvent) return;
    handleContextClose();

    const neighborTime = contextEvent.time;
    const tz = GetTimeZoneFromAbbreviation(props.timezone);
    const baseTime = neighborTime
      ? moment.utc(neighborTime).add(position === "above" ? -1 : 1, "minutes").toISOString()
      : null;

    // _localId survives VehicleHistory's id-remap (which overwrites `id`), so the parent can
    // still find this row when the user re-edits it before the first server save.
    const localId = `insert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const blankEvent = {
      id: localId,
      _localId: localId,
      eventId: null,
      typeId: null,
      comments: null,
      time: baseTime,
      routeLocation: null,
      odometer: null,
      closingComments: null,
      isActive: true,
      _isInsert: true,
      _insertNearIndex: contextIndex,
      _insertPosition: position,
    };

    setInsertAt({ index: contextIndex, event: blankEvent });
    setEditingEventId(null);
  };

  const handleEdit = () => {
    const evtId = contextEvent?.eventId ?? contextEvent?.id;
    handleContextClose();
    setEditingEventId(evtId);
    setInsertAt(null);
  };

  const handleRemove = () => {
    const evt = contextEvent;
    handleContextClose();
    if (props.onEventRemove) {
      props.onEventRemove(evt);
    }
  };

  const handleInlineSave = (updatedEvent: any) => {
    setEditingEventId(null);
    setInsertAt(null);
    if (props.onEventSave) {
      props.onEventSave(updatedEvent);
    }
  };

  const handleInlineCancel = () => {
    setEditingEventId(null);
    setInsertAt(null);
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, event: any) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, event: any) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const pos = e.clientY < rect.top + rect.height / 2 ? "top" : "bottom";
    setDropTargetId(event.eventId ?? event.id);
    setDropPosition(pos);
  };

  const handleDrop = (e: React.DragEvent, targetEvent: any) => {
    e.preventDefault();
    if (!draggedEvent || !props.onEventsReorder) {
      resetDrag();
      return;
    }

    const draggedId = draggedEvent.eventId ?? draggedEvent.id;
    const targetId = targetEvent.eventId ?? targetEvent.id;
    if (draggedId === targetId) {
      resetDrag();
      return;
    }

    // Work with only timeline events from orderedActivity
    const timelineEvents = (props.orderedActivity || []).filter(
      (m: any) => !Object.hasOwn(m, "activityId")
    );

    const draggedIndex = timelineEvents.findIndex(
      (e: any) => (e.eventId ?? e.id) === draggedId
    );
    const targetIndex = timelineEvents.findIndex(
      (e: any) => (e.eventId ?? e.id) === targetId
    );
    if (draggedIndex === -1 || targetIndex === -1) {
      resetDrag();
      return;
    }

    const reordered = [...timelineEvents];
    const [removed] = reordered.splice(draggedIndex, 1);

    let insertIndex;
    if (dropPosition === "bottom") {
      insertIndex = draggedIndex < targetIndex ? targetIndex : targetIndex + 1;
    } else {
      insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
    }
    insertIndex = Math.max(0, Math.min(insertIndex, reordered.length));
    reordered.splice(insertIndex, 0, removed);

    // Anchor the dropped event to the one above it (above.time + 1 min). Only fall back
    // to below.time - 1 min when the drop lands at the very top.
    const above = insertIndex > 0 ? reordered[insertIndex - 1] : null;
    const below = insertIndex < reordered.length - 1 ? reordered[insertIndex + 1] : null;
    let newTime = removed.time;
    if (above?.time) {
      newTime = moment.utc(above.time).add(1, "minute").toISOString();
    } else if (below?.time) {
      newTime = moment.utc(below.time).subtract(1, "minute").toISOString();
    }
    if (newTime !== removed.time) {
      reordered[insertIndex] = { ...reordered[insertIndex], time: newTime };
    }

    resetDrag();
    props.onEventsReorder(reordered);
  };

  const resetDrag = () => {
    setDraggedEvent(null);
    setDropTargetId(null);
  };

  const editable = !!props.editable;

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(17)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" width="98%" sx={{ my: 2, mx: 1, overflow: "hidden" }} />
      ))}
    </Box>
  );

  const keyDown = (params, event) => {
    if (event.code === "ArrowDown" && props.selectedIndex < props.orderedActivity?.length) {
      props.setSelectedRow(props.orderedActivity[props.selectedIndex + 1]);
      props.setSelectedIndex(props.selectedIndex + 1);
      event.preventDefault();
    } else if (event.code === "ArrowUp" && props.selectedIndex > 0) {
      props.setSelectedRow(props.orderedActivity[props.selectedIndex - 1]);
      props.setSelectedIndex(props.selectedIndex - 1);
      event.preventDefault();
    }
  };

  const rowClicked = (data, index) => {
    props.setSelectedRow(data);
    props.setSelectedIndex(index);
    // Stops and timeline events are single points — clicking pans the map to
    // them. Driving segments (Activity rows where the state isn't "Stopped")
    // are spans on the route, so clicking them toggles the highlight overlay
    // instead — that was the original "as additional segments are highlighted
    // keep showing them" feature.
    const isActivity = Object.hasOwn(data, "activityId");
    const isStop = isActivity && data?.activityState === "Stopped";
    const isEvent = !isActivity;
    if (isStop || isEvent) {
      props.onPanToRow?.(data);
    } else {
      props.onToggleHighlight?.(data);
    }
  };

  const isHighlighted = (m: any): boolean => !!(props.highlightedRowIds && props.highlightedRowIds.has?.(m?.id));

  const isTimelineEvent = (m: any) => !Object.hasOwn(m, "activityId");

  const renderInlineEdit = (event: any) => (
    <EventInlineEdit
      event={event}
      eventTypes={props.eventTypes || []}
      waitReasons={props.waitReasons || []}
      pointsOfService={props.pointsOfService || []}
      timeZone={props.timezone}
      onSave={handleInlineSave}
      onCancel={handleInlineCancel}
    />
  );

  const renderInsertForm = (position: "above" | "below", i: number) => {
    if (!insertAt) return null;
    if (insertAt.index !== i) return null;
    if (
      (position === "above" && insertAt.event._insertPosition !== "above") ||
      (position === "below" && insertAt.event._insertPosition !== "below")
    )
      return null;

    return renderInlineEdit(insertAt.event);
  };

  const renderActivityCard = (m: any, i: number) => (
    <Grid
      key={i}
      container
      spacing={1}
      tabIndex={-1}
      onKeyDown={(e) => keyDown(m, e)}
      onClick={() => rowClicked(m, i)}
      sx={{ outline: "none" }}
      data-activity-index={i}
    >
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Grid
          container
          spacing={1}
          sx={{
            border: 1,
            borderColor: isHighlighted(m) ? "#1a237e" : "grey.500",
            borderRadius: "5px",
            backgroundColor: isHighlighted(m) ? "#e8eaf6" : "inherit",
          }}
          className={props.selectedIndex === i ? "selected-row" : ""}
        >
          <Grid item>
            <Typography
              title={m.activityState}
              className={
                "background-circle " +
                (m.activityState == "Stopped" ? "background-error" : "background-success")
              }
            >
              {m.index}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Grid container>
              <Grid item xs={12} lg={6}>
                {moment
                  .utc(m.startDateUTC)
                  .tz(GetTimeZoneFromAbbreviation(props.timezone))
                  .format("MM/DD HH:mm")}
                {" - "}
                {moment
                  .utc(m.endDateUTC)
                  .tz(GetTimeZoneFromAbbreviation(props.timezone))
                  .format("MM/DD HH:mm")}
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box component="div" sx={{ display: "inline" }}>
                  Duration:{" "}
                </Box>
                <Box sx={{ display: "inline", fontWeight: "500" }}>
                  {m.duration_m > 60
                    ? `${numFormat2Decimals.format(m.duration_m / 60)} hr`
                    : `${m.duration_m} min`}
                </Box>
                <Box component="div" sx={{ display: "inline", ml: 2 }}>
                  Distance:{" "}
                </Box>
                <Box component="div" sx={{ display: "inline", fontWeight: "500" }}>
                  {m.distance_mi} mi
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box component="div" sx={{ display: "inline" }}>
                  Odo Start:{" "}
                </Box>
                <Box component="div" sx={{ display: "inline", fontWeight: "500" }}>
                  {" "}
                  {m.gpsOdometerMilesStart}
                </Box>
                <Box component="div" sx={{ display: "inline", ml: 2 }}>
                  Odo End:{" "}
                </Box>
                <Box component="div" sx={{ display: "inline", fontWeight: "500" }}>
                  {" "}
                  {m.gpsOdometerMilesEnd}
                </Box>
              </Grid>
              {m.activityState == "Stopped" && (
                <>
                  <Grid item xs={12} lg={6}>
                    <Box component="div" sx={{ display: "inline" }}>
                      Lat/Long:{" "}
                    </Box>
                    <Box component="div" sx={{ display: "inline", fontWeight: "500" }}>
                      {numFormat4Decimals.format(m.startLatitude)} /{" "}
                      {numFormat4Decimals.format(m.startLongitude)}
                    </Box>
                  </Grid>
                </>
              )}
              <Grid item xs={12} lg={6}>
                <Box component="div" sx={{ display: "inline" }}>
                  Client Point:{" "}
                </Box>
                <Box component="div" sx={{ display: "inline", fontWeight: "500" }}>
                  {m.clientPointName}
                </Box>
              </Grid>
               <Grid item xs={12} lg={6}>
                <Box component="div" sx={{ display: "inline" }}>
                  Miles to Client Point:{" "}
                </Box>
                <Box component="div" sx={{ display: "inline", fontWeight: "500" }}>
                  {m.milesToClientPoint}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderTimelineCard = (m: any, i: number) => {
    const evtId = m.eventId ?? m.id;
    const isEditing = editingEventId != null && evtId === editingEventId;

    if (isEditing) {
      return (
        <Box key={i}>
          {renderInlineEdit(m)}
        </Box>
      );
    }

    const evtKey = m.eventId ?? m.id ?? i;
    const isDragging = draggedEvent && (draggedEvent.eventId ?? draggedEvent.id) === evtKey;
    const isDropTarget = dropTargetId === evtKey;

    return (
      <Grid
        key={i}
        container
        spacing={1}
        tabIndex={-1}
        onKeyDown={(e) => keyDown(m, e)}
        onClick={() => rowClicked(m, i)}
        onContextMenu={editable ? (e) => handleContextMenu(e, m, i) : undefined}
        onDragOver={editable ? (e) => handleDragOver(e, m) : undefined}
        onDrop={editable ? (e) => handleDrop(e, m) : undefined}
        sx={{ outline: "none", opacity: isDragging ? 0.4 : 1 }}
        data-activity-index={i}
      >
        <Grid item xs={12} sx={{ mb: 2 }}>
          {renderInsertForm("above", i)}
          {isDropTarget && dropPosition === "top" && (
            <Box sx={{ height: 3, backgroundColor: "primary.main", borderRadius: 1, mb: 0.5 }} />
          )}
          <Grid
            container
            spacing={1}
            sx={{
              border: 1,
              borderColor: isHighlighted(m)
                ? "#1a237e"
                : m.isSuggested
                ? "warning.main"
                : "grey.500",
              borderRadius: "5px",
              backgroundColor: isHighlighted(m)
                ? "#e8eaf6"
                : m.isSuggested
                ? "#fff3cd"
                : "inherit",
            }}
            className={props.selectedIndex === i ? "selected-row" : ""}
          >
            {editable && (
              <Grid
                item
                sx={{ display: "flex", alignItems: "center", cursor: "grab", px: 0.5 }}
                draggable
                onDragStart={(e) => handleDragStart(e, m)}
                onDragEnd={resetDrag}
              >
                <DragIndicator sx={{ color: "action.active", fontSize: "18px" }} />
              </Grid>
            )}
            <Grid item sx={{ pb: 1 }}>
              <Typography title={m.isSuggested ? "Suggested Event" : "Event"} className={"background-circle " + (m.isSuggested ? "background-warning" : "background-info")}>
                {m.index}
              </Typography>
            </Grid>
            <Grid item>
              {moment.utc(m.time).tz(GetTimeZoneFromAbbreviation(props.timezone)).format("MM/DD HH:mm")}
              {m.isSuggested && <Box component="span" sx={{ ml: 1, color: "warning.dark", fontWeight: 500 }}>(suggested)</Box>}
            </Grid>
            <Grid item>{m.type}</Grid>
            <Grid item>{m.pointOfServiceName}</Grid>
            <Grid item>{m.comments}</Grid>
            {m.location?.latitude != null && m.location?.longitude != null && (
              <Grid item sx={{ color: "text.secondary", fontSize: "0.85em" }}>
                {numFormat4Decimals.format(m.location.latitude)} /{" "}
                {numFormat4Decimals.format(m.location.longitude)}
              </Grid>
            )}
          </Grid>
          {isDropTarget && dropPosition === "bottom" && (
            <Box sx={{ height: 3, backgroundColor: "primary.main", borderRadius: 1, mt: 0.5 }} />
          )}
          {renderInsertForm("below", i)}
        </Grid>
      </Grid>
    );
  };

  return !props.dataInitialized ? (
    <LoadingSkeleton />
  ) : (!props.orderedActivity || props.orderedActivity?.length === 0) ? (
    <>
      {/* A changed date window is still loading: say so rather than "no data". */}
      {props.activityLoading ? (
        <LinearProgress sx={{ mb: 1 }} />
      ) : (
        <Box sx={{ textAlign: "center", fontWeight: 500, fontSize: "1.25em" }}>No Activity Data Available</Box>
      )}
    </>
  ) : (
    <>
      <Box id={`activity-cards-container-${props.sourceIdPrefix || 'default'}`} sx={{ ml: 1 }}>
        <Paper
          sx={{
            p: 1,
            width: "99%",
            height: props.activityLoading && !props.orderedActivity?.length ? "82vh" : "auto",
            overflow: "hidden",
            fontSize: "14px",
          }}
        >
          {/* Loading GPS for a changed date window: the cards below are the previous window's. */}
          {props.activityLoading && <LinearProgress sx={{ mb: 1 }} />}
          {props.orderedActivity &&
            props.orderedActivity.map((m, i) =>
              Object.hasOwn(m, "activityId")
                ? renderActivityCard(m, i)
                : renderTimelineCard(m, i)
            )}
        </Paper>
      </Box>

      {editable && (
        <Menu
          open={contextMenu !== null}
          onClose={handleContextClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={() => handleInsert("above")}>
            <ListItemIcon><InsertAboveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Insert Above</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleInsert("below")}>
            <ListItemIcon><InsertBelowIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Insert Below</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEdit}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleRemove}>
            <ListItemIcon><RemoveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Remove</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};
export default VehicleActivityCards;
